<template>
    <div id="payment-method-form">
        <v-card>
            <v-card-title>
                <v-icon left :color="kingsGold">mdi-credit-card</v-icon>Payment Method
            </v-card-title>
            <v-alert v-if="!valid" :color="dangerColor" class="mr-3 ml-3 mb-0" dense icon="mdi-alert-circle" text
                prominent>
                There are one or more required form fields that are empty. Please verify that all required form fields
                are filled in.
            </v-alert>
            <v-card-text>
                <v-form ref="paymentMethodForm" v-model="valid" lazy-validation>
                    <v-container class="px-0" fluid>
                        <v-alert :color="infoColor" outlined text>
                            <div class="title">Payment Information</div>
                            <ol>
                                <li>
                                    A $1000 deposit is required to confirm you residence application. This deposit will
                                    be
                                    applied to your Residence fees. For information on accepted <b>payment methods</b>
                                    please visit
                                    <a href="https://www.kings.uwo.ca/current-students/money-matters/fees-and-responsibilities/payment-methods/"
                                        target="_blank">https://www.kings.uwo.ca/current-students/money-matters/fees-and-responsibilities/payment-methods/</a>
                                </li>
                                <li>
                                    <b>Please note:</b> your residence
                                    application cannot be processed and until the $1000 deposit is received.
                                </li>
                            </ol>
                            <div class="pt-3 title">Terms</div>
                            By submitting this form electronically, I agree to the following:
                            <ul>
                                <li>
                                    I authorize that to the best of my knowledge the information provided on this form is accurate and complete.
                                </li>
                                <li>
                                    I understand that I will be placed in a double room. Students who submit a Single
                                    Room / Super Single Room Special Needs Accommodation Request Form (see above) will be notified of their
                                    placement status by the end of June.
                                </li>
                            </ul>
                            King's University College wishes to ensure the full and fair implementation of the principles of
                            the Ontario Human Rights Code, which states: "every person has the right to equal treatment
                            with respect to services, goods and facilities, without discrimination because of race, ancestry,
                            place of origin, colour, ethnic origin, citizenship, creed, sex, sexual orientation, age,
                            same-sex partnership status, marital status, family status or disability".
                            <div class="pt-3 title">Please Note</div>
                            Your residence application will be processed when the Residence Office receives the
                            following:
                            <ol class="pb-3">
                                <li> A completed Residence Agreement and Application form (this form)</li>
                                <li>A $1000 deposit</li>
                            </ol>
                            You will receive a confirmation e-mail within 10 business days of submitting a completed
                            application package (including deposit). The e-mail will be sent to your <b>UWO e-mail account</b>.<br><br>
                            Your residence accommodation is <b>not</b> secured until you receive confirmation from the
                            Residence Office. Submitting the online form and providing the deposit <b>does not</b> guarantee a place in residence.
                            <div class="pt-3 title">Refund Policy</div>
                            Residence deposits are not an additional fee. The $1000 will be applied towards your residence room fee.  There are no refunds.
                            <!-- Refunds of 50% of the deposit ($300) are available until June 30, for September move ins, and November 30 for January move ins. 
                            After these dates the deposit is non-refundable. -->
                        </v-alert>
                        <v-select v-model="applicant.paymentMethod" :disabled="payment"
                            hint="Please review Payment Information above for important details"
                            :items="paymentMethodItems" label="Payment Method" persistent-hint
                            placeholder="Please select..." :rules="paymentMethodRules" required>
                        </v-select>
                        <section v-if="resolvePaymentMethod('Credit Card')">
                            <v-card-text class="pb-4 px-0">
                                <v-icon :color="kingsGold">mdi-credit-card-outline</v-icon> Credit Card Payment (Visa
                                and MasterCard)
                                <ul class="pt-2">
                                    <li>Due to the requirement to comply with Payment Card Industry (PCI) standards
                                        relating to
                                        data security, King's cannot accept credit card information in writing or over
                                        the
                                        phone.</li>
                                    <li>
                                        <b>Students from China</b> can now also pay with Credit Card, Debit Card or
                                        Pre-paid
                                        Card through Western Union; <v-icon :color="dangerColor" small>mdi-file-pdf
                                        </v-icon> <a
                                            href="https://www.kings.uwo.ca/kings/assets/File/depts/finance/sfs/Online%20China%20Payments-CAD%20Non-HigherOne.pdf"
                                            target="_blank">
                                            Click Here
                                        </a> for more details.
                                    </li>
                                </ul>
                            </v-card-text>
                            <v-select v-model="payment" :color="!payment ? dangerColor :successColor"
                                :item-color="!payment ? dangerColor :successColor" :items="paymentItems"
                                label="Residence Confirmation Deposit Status" outlined
                                :prepend-inner-icon="!payment ? 'mdi-close' : 'mdi-check'" readonly
                                :rules="paymentRules" required>
                            </v-select>
                            <online-payment v-if="!payment" :applicant="applicant"
                                @setCreditCardPayment="setCreditCardPayment" @setNotification="setNotification">
                            </online-payment>
                            <v-alert v-else :color="successColor" icon="mdi-credit-card-check" prominent text>
                                Credit card payment for the residence confirmation deposit of $1000.00 CAD has been
                                successfully processed.
                            </v-alert>
                        </section>
                        <section v-else-if="resolvePaymentMethod('Online Banking')">
                            <v-card-text class="px-0">
                                <v-icon :color="kingsGold">mdi-bank</v-icon> Online Banking
                                <ul class="pt-2">
                                    <li>
                                        <b>Telephone/Online Banking:</b> Use King's University College as the bill/payee
                                        and
                                        your Student Number as the account number:
                                        <a href="https://www.kings.uwo.ca/current-students/money-matters/fees-and-responsibilities/payment-methods/banking/"
                                            target="_blank">For more information and applicable banks click here
                                        </a>
                                        .
                                    </li>
                                    <li>
                                        <b>International Bank Transfer - GlobalPay:</b> If you’re an <b>International student</b>,
                                        paying your tuition can be easily done by clicking the following link to the
                                        GlobalPay for Students system : <a
                                            href="https://www.kings.uwo.ca/current-students/money-matters/fees-and-responsibilities/payment-methods/international-bank-transfers/"
                                            target="_blank">International Bank Transfer</a>. There is no
                                        processing
                                        fee associated with this payment method.
                                    </li>
                                    <li>
                                        <b>International Bank Transfer - PayMyTuition:</b> Tuition and fee payments from
                                        outside of Canada are available through PayMyTuition. With PayMyTuition,
                                        students can pay their tuition form any bank, in any country, in any currency at
                                        better than bank exchange rates. <a href="https://www.paymytuition.com/paynow/kings" target="_blank">Click here for more details</a>.
                                    </li>
                                    <li>
                                        <b>International Direct Wire Payment:</b> tudents are able to transfer money
                                        directly to King's University College's bank account upon request. Please send
                                        your request <b>from your UWO email account</b> to <a href="mailto:studentfinance@kings.uwo.ca">studentfinance@kings.uwo.ca</a> and
                                        ensure to include your full name and student number. King's University College
                                        will charge a processing fee of $75.00 for each direct wire transfer. All bank
                                        charges are the responsibility of the student.
                                    </li>
                                </ul>
                            </v-card-text>
                        </section>
                        <section v-else-if="resolvePaymentMethod('Cheque') || resolvePaymentMethod('Money Order')">
                            <v-card-text class="px-0">
                                <v-icon :color="kingsGold">mdi-checkbook</v-icon> Cheque/Draft Cheque/Money Order
                                <v-alert :color="infoColor" text prominent icon="mdi-information">
                                    <b>Please note:</b> The processing fee for all International Money Orders/Bank
                                    Drafts or Cheques will be $250.00. This fee will be added to the student's tuition
                                    account, and is due immediately upon receipt.
                                </v-alert>
                                By mailing a $1000 cheque or money order to the Residence Office (address below).
                                Cheques and money orders must be made payable to King's University College and include on
                                the front of the cheque the name and student number of the student requesting residence.
                                Please send your cheque or money order to:
                            </v-card-text>
                            <v-card-text class="font-weight-bold px-0">
                                Residence Office<br>
                                King's University College<br>
                                266 Epworth Ave.<br>
                                London,ON<br>
                                N6A 2M3
                            </v-card-text>
                            <v-card-text class="px-0">
                                <ul>
                                    <li>
                                        <b>Please make cheques payable to 'King's University College'.</b>
                                    </li>
                                    <li>
                                        <b>Important:</b> It is the students responsibility to ensure that cheques are
                                        properly completed.
                                        If a cheque is returned for any reason, the student will be subject to a $72.00
                                        return cheque charge and possible late fee. This penalty must be paid at the
                                        time the cheque is replaced. All replacement cheques must be certified.
                                    </li>
                                    <li>
                                        <b>Debit available at the counter:</b> For students who intend to use a debit
                                        card, please note that most debit cards have a default spending limit. If the
                                        payment you wish to make is larger than this limit, you may wish to contact the
                                        issuing bank beforehand to determine if you can increase your daily spending
                                        limit temporarily while you are trying to meet the fee payment deadline. If your
                                        bank will not do so, you will need to be prepared to make an alternate method of
                                        payment.
                                    </li>
                                    <li>
                                        <b>HigherEdPoints:</b> Further information and instructions available online at;
                                        <a href="http://www.higheredpoints.com/"
                                            target="_blank">http://www.higheredpoints.com/</a>
                                    </li>
                                </ul>
                            </v-card-text>
                        </section>
                    </v-container>
                </v-form>
            </v-card-text>
            <announcement :announcement="announcement"></announcement>
            <v-card-actions>
                <v-spacer>
                </v-spacer>
                <v-btn class="white--text" :color="infoColor" :disabled="!valid" @click="openSubmitDialog()">
                    Submit
                </v-btn>
                <v-btn class="white--text" :color="kingsGrey" @click="prevStep(6)">
                    Previous
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-dialog v-model="dialog" max-width="540">
            <v-card>
                <v-card-title>Confirmation</v-card-title>
                <v-card-text>
                    Please ensure that the information provided is accurate and reliable. If you wish to submit the
                    Online Residence Application, please click confirm.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn :color="successColor" :loading="submitBool" text @click="submitForm()">
                        Confirm
                    </v-btn>
                    <v-btn :color="warningColor" text @click="closeSubmitDialog()">
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import Announcement from '../Announcement';
import OnlinePayment from '../Payment/OnlinePayment';

export default {
    name: 'payment-method-form',
    components: {
        Announcement,
        OnlinePayment
    },
    props: {
        announcement: {
            required: true,
        },
        applicant: {
            required: true
        },
        submitBool: {
            required: true
        }
    },
    data() {
        return {
            dialog: false,
            payment: false,
            paymentItems: [{
                    text: 'Deposit has yet to be paid via credit card',
                    value: false
                },
                {
                    text: 'Residence confirmation deposit has been successfully paid',
                    value: true
                }
            ],
            paymentRules: [
                v => !!v || 'Credit card payment is required'
            ],
            paymentMethodItems: [{
                    text: 'Credit Card',
                    value: 'Credit Card'
                },
                {
                    text: 'Online Banking (through your bank)',
                    value: 'Online Banking'
                },
                {
                    text: 'Cheque',
                    value: 'Cheque'
                },
                {
                    text: 'Money Order',
                    value: 'Money Order'
                }
            ],
            paymentMethodRules: [
                v => !!v || 'Payment method is required'
            ],
            valid: true
        }
    },
    methods: {
        closeSubmitDialog() {
            this.dialog = false;
        },
        openSubmitDialog() {
            if (this.$refs.paymentMethodForm.validate()) {
                this.dialog = true;
            }
        },
        prevStep(val) {
            this.$emit('setStep', val);
            this.$nextTick(() => {
                this.$emit('scrollToTop');
            })
        },
        resolvePaymentMethod(type) {
            if (this.applicant && this.applicant.hasOwnProperty('paymentMethod') && this.applicant.paymentMethod == type) {
                return true;
            }

            return false;
        },
        setCreditCardPayment() {
            this.payment = true;
        },
        setNotification(color, icon, message, status) {
            this.$emit('setNotification', color, icon, message, status);
        },
        submitForm() {
            this.$emit('submitForm');
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        infoColor() {
            return '#5578eb';
        },
        kingsGold() {
            return '#d7ae28';
        },
        kingsGrey() {
            return '#6d6e71';
        },
        successColor() {
            return '#1bc5bd';
        },
        warningColor() {
            return '#ffa800'
        }
    }
}
</script>